import { useEffect, useRef, useState } from 'react';
import { api, networkWords } from '../api.js';
import { effectiveParam } from '../params.js';
import { DoseCalculator } from './DoseCalculator.jsx';
import { DoseLogSheet } from './DoseLogSheet.jsx';
import { groupReadings, currentReadingsClient } from '../utils.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { canRemoveDose, removeDoseConfirm, removedDoseNote } from '../dose-display.js';
import { doseLogUnitLabel } from '../../../shared/dose-log.js';
import { isQuarantineTank } from '../../../shared/tank-purpose.js';
import { tankSubtype } from '../../../shared/tank-subtype.js';
import { FRESHWATER_SUBTYPES } from '../../../shared/param-explainers.js';
import { paramLabelLower } from '../../../shared/param-labels.js';

// Dedicated dose-management surface. Lists the three dose-able
// parameters (alk / Ca / Mg) with their latest readings + targets,
// plus a "Calculate dose" entry on each card. Recent dose history at
// the bottom so the keeper can see what they've already added.
//
// Phase-aware: in setup / cycling, shows a "no dosing needed yet"
// banner because the tank isn't consuming yet. Same gate the
// preview endpoint enforces server-side.

// Tank params are keyed alk/calcium/magnesium (the dose API uses
// alk/ca/mg; DoseCalculator translates at the boundary). Iterating the
// tank keys here is what makes the Calcium and Magnesium cards render —
// they were previously missing because effectiveParam(tank, 'ca'|'mg')
// always returned null.
const DOSABLE_PARAMS = ['alk', 'calcium', 'magnesium'];

// The dose API's keys, as words, for the history list.
const DOSE_KEY_PARAM = { ca: 'calcium', mg: 'magnesium' };

// Reef dosing does not fit a quarantine / hospital tank or a freshwater
// one; the server refuses it there too (not_a_reef_tank).
export function usesReefDosing(tank) {
  if (isQuarantineTank(tank)) return false;
  const slug = tankSubtype(tank);
  return !(slug && FRESHWATER_SUBTYPES.includes(slug));
}

const PARAM_DESC = {
  alk: 'Alkalinity — buffer the corals consume to build skeleton.',
  calcium: 'Calcium — the other half of the consumption pair.',
  magnesium: 'Magnesium — stabiliser, slow-moving, but lets the other two stick.',
};

export function DoseView({ tank, onReload }) {
  const [doseParam, setDoseParam] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [historyTick, setHistoryTick] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteErr, setDeleteErr] = useState('');
  const [removedNote, setRemovedNote] = useState('');
  const noteRef = useRef(null);
  // After a Remove far down the list the line saying what happened sat
  // off screen.
  useEffect(() => {
    if (removedNote) noteRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [removedNote]);
  const confirm = useConfirm();
  const removeDose = async (h) => {
    const ok = await confirm({
      ...removeDoseConfirm(h, formatSavedAt(h.ts)),
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setDeleteErr('');
    setRemovedNote('');
    setDeletingId(h.id);
    let out;
    try {
      out = await api(`/api/tanks/${tank.id}/dose-events/${h.id}`, { method: 'DELETE' });
    } catch (e) {
      setDeletingId(null);
      // Removed already, on another phone: the list catches up.
      if (e.status === 404) {
        setRemovedNote('That dose was already removed.');
        setHistoryTick((t) => t + 1);
        return;
      }
      setDeleteErr(networkWords(e) || e.message || 'Could not remove that dose.');
      return;
    }
    setDeletingId(null);
    setRemovedNote(removedDoseNote(out?.bottles));
    setHistoryTick((t) => t + 1);
    // A bottle that got its amount back shows on Home; a refresh that
    // fails is not a failed removal.
    if (out?.bottles?.some((b) => b.restored != null)) {
      Promise.resolve(onReload?.()).catch(() => {});
    }
  };

  const phase = tank?.phase || null;
  const reef = usesReefDosing(tank);
  // A viewer's preview answers 403, which the calculator read as "check your
  // entries" (nightly sweep 2, 2026-10-24): the cards stay readable, closed.
  const viewer = tank?.role === 'viewer';
  const blocked = reef && (phase === 'setup' || phase === 'cycling');

  useEffect(() => {
    let cancelled = false;
    setLoadingHistory(true);
    api(`/api/tanks/${tank.id}/dose-events?limit=20`)
      .then((rows) => {
        if (cancelled) return;
        setHistory(rows || []);
      })
      .catch(() => {
        if (cancelled) return;
        setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tank.id, doseParam, historyTick]);

  // Pull latest reading per param from tank.readings (already loaded
  // by AppShell) — no extra round trip.
  // The keeper's own recent test before a Trident number with no test time
  // (shared/trident-trust.js), as the server's dose baseline reads it.
  const latestByParam = {};
  for (const [k, list] of Object.entries(groupReadings(tank.readings || []))) {
    const cur = currentReadingsClient(list, k)[0];
    if (cur) latestByParam[k] = cur;
  }

  return (
    <div className="dv" style={{ padding: '0 20px 32px' }}>
      <header className="dv-header">
        <div className="dv-eyebrow">Dose</div>
        <h2 className="dv-title font-heading">{reef ? 'Dose calculator' : 'Doses'}</h2>
        {reef ? (
          <p className="dv-intro">
            BRS-grounded math. Sensei never auto-doses — you preview the number, add the liquid
            yourself, then confirm so the next test reads against this dose.
          </p>
        ) : (
          <p className="dv-intro">
            This tank does not use reef dosing, so there is no alkalinity, calcium or magnesium
            calculator here. Log anything you add below.
          </p>
        )}
      </header>

      {blocked && (
        <div className="dv-block">
          <strong>No calculated doses during {phase}.</strong> Your tank isn't using much yet, so
          let the cycle finish first; the calculator opens here once it is done. Anything you do
          add, bottled bacteria for example, you can still log below.
        </div>
      )}

      {viewer && reef && !blocked && (
        <div className="dv-block">
          Only the tank owner or an editor can calculate and log doses.
        </div>
      )}
      <div className="dv-cards">
        {(reef ? DOSABLE_PARAMS : []).map((k) => {
          const p = effectiveParam(tank, k);
          if (!p || p.hidden) return null;
          const latest = latestByParam[k];
          const cur = latest?.value;
          const target = p.target;
          const off =
            Number.isFinite(cur) &&
            Array.isArray(p.acceptable) &&
            (cur < p.acceptable[0] || cur > p.acceptable[1]);
          const closed = blocked || viewer || !latest;
          return (
            <button
              type="button"
              key={k}
              className={`dv-card ${off ? 'dv-card-off' : ''}`}
              onClick={() => !closed && setDoseParam(k)}
              disabled={closed}
              aria-label={`Calculate ${p.label} dose`}
            >
              <div className="dv-card-head">
                <div className="dv-card-label font-heading">{p.label}</div>
                {/* An arrow on a card that cannot open reads as a way in. */}
                {!closed && (
                  <div className="dv-card-arrow" aria-hidden="true">
                    →
                  </div>
                )}
              </div>
              <div className="dv-card-row">
                <div className="dv-card-val">
                  <span className="dv-card-num">{Number.isFinite(cur) ? cur : '—'}</span>
                  <span className="dv-card-unit">{p.unit}</span>
                </div>
                <div className="dv-card-target">
                  target {target}
                  {p.unit ? ` ${p.unit}` : ''}
                </div>
              </div>
              <div className="dv-card-desc">{PARAM_DESC[k]}</div>
              {!latest && (
                <div className="dv-card-no-reading">
                  No reading yet — log one first to compute a dose.
                </div>
              )}
            </button>
          );
        })}
      </div>

      <section className="dv-history">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <h3 className="dv-history-head font-heading">Recent doses</h3>
          {/* A viewer's log would only answer 403. */}
          {tank.role !== 'viewer' && (
            <button
              type="button"
              className="ts-chip"
              style={{ minHeight: 44 }}
              onClick={() => setLogOpen(true)}
            >
              + Log another dose
            </button>
          )}
        </div>
        {tank.role !== 'viewer' && (
          <p style={{ fontSize: 14, color: 'var(--text-dim)', margin: '4px 0 10px' }}>
            Dosing something not listed above? Log it here: trace elements, iodine, nitrate, any
            brand.
          </p>
        )}
        {deleteErr && (
          <div role="alert" className="ts-err" style={{ marginBottom: 8 }}>
            {deleteErr}
          </div>
        )}
        {removedNote && (
          <div
            ref={noteRef}
            role="status"
            style={{ fontSize: 15, color: 'var(--text-dim)', marginBottom: 8, scrollMarginTop: 80 }}
          >
            {removedNote}
          </div>
        )}
        {loadingHistory ? (
          <div className="dv-history-loading">Loading…</div>
        ) : history.length === 0 ? (
          <div className="dv-history-empty">No doses logged yet.</div>
        ) : (
          <ul className="dv-history-list">
            {history.map((h) => (
              <li key={h.id} className="dv-history-row">
                <div className="dv-history-when">{formatRelative(h.ts)}</div>
                <div className="dv-history-amount">
                  <strong>
                    {h.method === 'log' && h.amount != null
                      ? `${Number(h.amount)} ${doseLogUnitLabel(h.unit)}`
                      : `${h.amount_ml} mL`}
                  </strong>
                  {h.param && h.param !== 'other' && (
                    <span> · {paramLabelLower(DOSE_KEY_PARAM[h.param] || h.param)}</span>
                  )}
                </div>
                {h.product_label && <div className="dv-history-product">{h.product_label}</div>}
                {!canRemoveDose(h) && (
                  <div className="dv-history-product">From your controller</div>
                )}
                {canRemoveDose(h) && tank.role !== 'viewer' && (
                  <button
                    type="button"
                    className="ts-chip"
                    style={{ minHeight: 44, marginTop: 4 }}
                    disabled={deletingId === h.id}
                    onClick={() => removeDose(h)}
                  >
                    {deletingId === h.id ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {logOpen && (
        <DoseLogSheet
          tank={tank}
          recent={history}
          onClose={() => {
            setLogOpen(false);
            // A save left unsure shows here if it went through.
            setHistoryTick((t) => t + 1);
          }}
          onDone={async () => {
            setRemovedNote('');
            await onReload?.();
          }}
        />
      )}

      {doseParam && (
        <DoseCalculator
          tank={tank}
          param={doseParam}
          latestValue={latestByParam[doseParam]?.value ?? null}
          onClose={() => setDoseParam(null)}
          onReload={onReload}
        />
      )}
    </div>
  );
}

// The confirm names the dose by its own time, so a wet thumb on the wrong
// row of a daily dose is caught.
function formatSavedAt(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const old = Date.now() - d.getTime() > 6 * 86400000;
  return d.toLocaleString(undefined, {
    ...(old ? { month: 'short', day: 'numeric' } : { weekday: 'short' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelative(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
